import { PostApiIntroducerPortalIntroducerAccountRequestBody } from '@iwoca/lapi-client/introducer_portal';
import { fullRegistrationSchemaUK } from 'components/Registration/registrationSchema';
import {
  lendingApiFetch,
  lendingApiFetchJson,
  postIntroducerDocuments,
} from 'lendingApi/lendingApi';
import { IwQueryClient } from 'store/IwQueryClient';
import { DuplicateEmailError, mintCognitoUserForSignup } from 'utils/authApi';
import { URLS } from 'utils/tests/urls.config';
import * as yup from 'yup';
import {
  TPatchIntroducerAccountParams,
  TSaveDocumentsParams,
  TSubmitRegistrationParams,
} from './registrationForm.types';

export const schema = fullRegistrationSchemaUK.shape({
  primarilyCommercialFinance: yup
    .string()
    .oneOf(['true', 'false'])
    .required('Selection is required'),
  businessProvision: yup.string().when('primarilyCommercialFinance', {
    is: (value: string) => value === 'false',
    then: (schema) =>
      schema.required(
        () => 'Please describe the type of finance your business offers',
      ),
  }),
  ICOReference: yup.string().required('ICO registration number is required'),
  commissionReportEmail: yup
    .string()
    .required('Commission report email address is required'),
  certifiedIdFile: yup.mixed().required('Certified ID is required'),
  bankStatementFile: yup.mixed().required('Bank statement is required'),
  proofOfAddressFile: yup
    .mixed()
    .required('Proof of business address is required'),
});

export const postIntroducerAccount = async ({
  values,
  onError,
  onDuplicateEmail,
}: TSubmitRegistrationParams) => {
  const requestBody: PostApiIntroducerPortalIntroducerAccountRequestBody = {
    data: {
      introducer_account: {
        introducer_type: values.introducerType,
        public_facing_company_name: values.companyTradingName,
        address: values.registeredTradingAddress,
        company_name: values.companyName,
        company_number: values.companyNumber,
        email: values.email,
        fca_reference: values.FCAReferenceNumber,
        phone_number: values.phoneNumber,
        post_code: values.postcode,
        website: values.companyWebsite,
        ico_reference: values.ICOReference,
        primarily_commercial_finance:
          values.primarilyCommercialFinance === 'true'
            ? true
            : values.primarilyCommercialFinance === 'false'
              ? false
              : undefined,
        business_provision: values.businessProvision,
        commission_report_email: values.commissionReportEmail,
        onboarding_files_uploaded: false,
      },
      introducer: {
        title: values.title,
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        phone_number: values.phoneNumber,
      },
    },
  };
  try {
    const signedUpWithCognito = await mintCognitoUserForSignup(values.email);
    const url = signedUpWithCognito
      ? URLS.signupIntroducerAccount()
      : URLS.postIntroducerAccount();
    return await lendingApiFetchJson(url, {
      method: 'post',
      body: JSON.stringify({
        ...requestBody,
      }),
    });
  } catch (error) {
    if (error instanceof DuplicateEmailError) {
      onDuplicateEmail();
      return;
    }
    onError();
  }
};

export const handleSaveDocuments = async ({
  selectedFiles,
  introducerId,
}: TSaveDocumentsParams) => {
  await Promise.all(
    selectedFiles.map(async (selectedFile) => {
      const body = new FormData();

      body.append('document', selectedFile.file);
      body.append('document_type', selectedFile.docType);

      try {
        await postIntroducerDocuments({
          introducerId,
          body,
        });
      } catch {
        throw new Error();
      }
    }),
  );
};

export const handlePatchOnboardingFilesUploaded = async ({
  introducerAccountId,
}: TPatchIntroducerAccountParams) => {
  try {
    await lendingApiFetch(
      URLS.patchIntroducerAccount({ introducerAccountId }),
      {
        method: 'PATCH',
        body: JSON.stringify({
          data: {
            onboarding_files_uploaded: true,
          },
        }),
      },
    );
    IwQueryClient.getOrCreate().invalidateQueries({
      queryKey: ['introducer'],
    });
  } catch {
    throw new Error();
  }
};
